import { styled } from '@mui/material';
import { type ReactNode, useId } from 'react';

import { MEETING_LIST_CONTAINER } from './meetingListContainer';

type Props = {
  children: ReactNode;
  /** Visible heading of the group (e.g. a year). Names the list for screen readers. */
  title?: string;
  /** Accessible name when the list has no visible `title`. */
  'aria-label'?: string;
  'aria-busy'?: boolean;
  'data-testid'?: string;
};

const Root = styled('div')({
  containerType: 'inline-size',
  containerName: MEETING_LIST_CONTAINER,
});

const Heading = styled('h3')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  margin: 0,
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(2),
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.elevation1,
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontVariantNumeric: 'tabular-nums',
}));

const List = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  '& > li + li': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const Group = styled('div')(({ theme }) => ({
  '& + &': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Register of `MeetingListItem` rows separated by hairline dividers, optionally under a visible
 * group heading (e.g. the year). Rows stack on narrow widths and switch to aligned columns once
 * the list itself is wide enough (container query, not viewport).
 */
export function MeetingList(props: Props) {
  const headingId = useId();

  return (
    <Group data-testid={props['data-testid'] ?? 'meeting-list'}>
      <Root>
        {props.title ? <Heading id={headingId}>{props.title}</Heading> : null}
        <List
          aria-labelledby={props.title ? headingId : undefined}
          aria-label={props.title ? undefined : props['aria-label']}
          aria-busy={props['aria-busy']}
        >
          {props.children}
        </List>
      </Root>
    </Group>
  );
}

export type { Props as MeetingListProps };
